import React, { Suspense, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { TranslateFunction } from 'react-localize-redux';

import {
  LoyaltyRequiredCondition,
  LoyaltyVoucher,
} from '../../../core/state/store/store.types';
import { Button } from '../../atoms/Button';
import { Typography } from '../../atoms/Typography';
import { Present as PresentIcon } from '../../icons/Present';
import { LoadingSpinner } from '../../layouts/LoadingSpinner';
import { Popout } from '../../layouts/Popout';
import { LoyaltyProgressBar } from '../../molecules/LoyaltyProgressBar';
import { LoyaltyVoucherDialog } from '../../partials/LoyaltyVoucherDialog';

const useStyles = makeStyles((theme: Theme) => ({
  actionText: {
    fontSize: `${theme.typography.pxToRem(13)} !important`,
  },
  bottomRow: {
    alignItems: 'flex-start',
    display: 'flex',
    height: '60px',
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  button: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  icon: {
    color: theme.palette.grey[400],
    fontSize: '66px',
    marginTop: theme.spacing(0.5),
  },
  buttonGroup: {
    paddingTop: theme.spacing(1.5),

    '& button:nth-child(2)': {
      marginLeft: theme.spacing(1.5),
    },
  },
  loyaltyBarContainer: {
    marginTop: theme.spacing(1),
  },
  popout: {
    '& .fd-popout__children': {
      paddingBottom: 0,
    },
  },
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
}));

export type LoyaltyProgressPopoutProps = {
  conditions: LoyaltyRequiredCondition;
  isPending: boolean;
  onClosePopout: () => void;
  pointerVariant?: 'right' | 'center';
  showPopout: boolean;
  title: string;
  translate: TranslateFunction;
  voucher?: LoyaltyVoucher;
  voucherDescription?: string;
};

export function LoyaltyProgressPopout(
  props: LoyaltyProgressPopoutProps,
): JSX.Element {
  const {
    conditions,
    isPending,
    onClosePopout,
    pointerVariant = 'right',
    showPopout,
    title,
    translate,
    voucher,
    voucherDescription,
  } = props;
  const classes = useStyles();
  const [showVoucherDialog, setShowVoucherDialog] = useState<boolean>(false);
  const { currentValue, requiredValue } = conditions;

  const toggleVoucherDialog = (): void =>
    setShowVoucherDialog(!showVoucherDialog);

  const getButton = (): JSX.Element => {
    const hasFinalStarPending =
      isPending && parseInt(requiredValue) - parseInt(currentValue) === 1;
    if (voucher?.code) {
      return (
        <div className={classes.bottomRow}>
          <Button
            className={classes.button}
            size="small"
            onClick={(): void => toggleVoucherDialog()}
          >
            {translate('LoyaltyProgressPopout_viewVoucherButtonTitle')}
          </Button>
          <PresentIcon className={classes.icon} />
        </div>
      );
    }
    if (hasFinalStarPending) {
      return (
        <Button
          className={classes.button}
          disabled
          size="small"
          onClick={(): void => toggleVoucherDialog()}
          variant="secondary"
        >
          {translate('LoyaltyProgressPopout_pendingVoucherButtonTitle')}
        </Button>
      );
    }

    return (
      <Button className={classes.button} onClick={() => {}} variant="text">
        <Typography className={classes.actionText}>
          {translate('LoyaltyCard_HowLoyaltyWorks')}
        </Typography>
      </Button>
    );
  };
  return (
    <Popout
      closeIconAriaLabel={translate('Popout_closeButtonAriaLabel') as string}
      className={classes.popout}
      pointerVariant={pointerVariant}
      onClosePopout={onClosePopout}
      popoutName="loyalty-progress-popout"
      showCloseButton
      showPopout={showPopout}
    >
      <div className={classes.root}>
        <div>
          <Typography variant="h3">{title}</Typography>
          <div className={classes.loyaltyBarContainer}>
            <LoyaltyProgressBar
              currentStar={parseInt(currentValue)}
              showPendingStar={isPending}
              totalStars={parseInt(requiredValue)}
            />
          </div>
          {getButton()}
        </div>
        {showVoucherDialog && (
          <Suspense fallback={<LoadingSpinner />}>
            <LoyaltyVoucherDialog
              details={voucherDescription}
              expiry={voucher.expireDate}
              onClose={toggleVoucherDialog}
              open={showVoucherDialog}
              translate={translate}
              voucherCode={voucher.code}
            />
          </Suspense>
        )}
      </div>
    </Popout>
  );
}
